// One SSE manager. The wire state has to tell the truth: `live` when at
// least one stream is receiving frames; `reconnecting` when a stream we
// asked for isn't (and the backoff is walking); `offline` when the device
// says it has no network.
//
// V3 recorded a rebuild of this; real daily use surfaced three ways it
// still lied on the phone (V4 §4):
//
//   1. Route transitions closed one stream then opened another; in the
//      gap `openStreams > 0 && liveStreams === 0` flashed "reconnecting"
//      even though nothing was actually wrong. Debounced now — only
//      genuine silence counts.
//
//   2. When the browser backgrounded the tab, iOS/Chrome silently
//      suspended EventSource. On foreground the `es` handle was still
//      truthy and `live` still true, so we didn't reconnect and didn't
//      show the truth. Now every visibilitychange back to visible
//      force-closes and reconnects any stream whose last frame is older
//      than 25 seconds.
//
//   3. Some CDN/proxy paths (the box's Cloudflare Tunnel is one) hold
//      TCP open while dropping all frames. `es.readyState === OPEN` says
//      nothing about the wire's aliveness. The daemon sends a heartbeat
//      every 20s; if the client sees NO bytes on a stream for 50s, we
//      treat that stream as dead and reconnect it, regardless of
//      readyState.
//
// V6 §A found two more, and they were the loud ones:
//
//   4. The heartbeat the 45s watchdog was written against was an SSE
//      COMMENT (`:hb`), and EventSource fires no listener for a comment.
//      So `lastByteAt` never advanced on a healthy but quiet stream and
//      the watchdog force-reconnected it about every 45 seconds, forever.
//      Every reconnect replayed the log, which is what re-stamped a job's
//      history with the reconnect's minute. The daemon now sends a named
//      `hb` event too and `openStream` always listens for it, whatever
//      handlers the caller passed.
//
//   5. `offline` was reached the moment every stream was momentarily
//      down, so one dropped frame during a backoff read as "no signal".
//      A stream now carries `downSince`, and only sustained loss is
//      offline; anything shorter is reconnecting, which is what it is.

export interface StreamHandle {
  close: () => void;
}

export type WireState = 'live' | 'reconnecting' | 'offline';

type WireListener = (state: WireState) => void;
const wireListeners = new Set<WireListener>();

// Per-stream state, kept in a set so global calculations are truthful even
// while pages mount and unmount.
interface TrackedStream {
  live: boolean;
  lastByteAt: number;
  /** When this stream last stopped being live, or 0 while it is live. */
  downSince: number;
  reconnect: () => void;
}

/* Ops V7 design §5: the daemon heartbeats every 15 seconds; the offline
   banner appears only after 45 seconds without one and disappears the
   moment the stream is back. */
/** How long every stream must stay down before the app says so. */
const OFFLINE_AFTER_MS = 45_000;
/** No observable byte for this long means the socket is lying. */
const SILENT_AFTER_MS = 45_000;
const streams = new Set<TrackedStream>();

let currentState: WireState = 'live';
let flipTimer: number | null = null;

/* The wire's word alone (Ops V7 design §5): offline means every stream has
   been silent for 45 seconds, not that the device reports no network — the
   device's flag is only a reason to re-check sooner. */
function computeState(): WireState {
  if (streams.size === 0) return 'live';
  // Silence is measured from the last byte any stream received, whatever
  // the socket claims: a connection a proxy holds open while dropping
  // frames is exactly as silent as one that is closed.
  const freshest = Math.max(...[...streams].map((s) => s.lastByteAt));
  if (Date.now() - freshest > OFFLINE_AFTER_MS) return 'offline';
  const anyLive = [...streams].some((s) => s.live);
  return anyLive ? 'live' : 'reconnecting';
}

function scheduleWireCheck(): void {
  const next = computeState();
  if (next === currentState) return;
  // Flipping TO reconnecting is debounced by 800ms so a route change
  // (close-then-open in the same tick) never flashes red on the phone.
  // Flipping TO live is immediate — the truthful side.
  if (next === 'live' || next === 'offline') {
    if (flipTimer !== null) {
      clearTimeout(flipTimer);
      flipTimer = null;
    }
    currentState = next;
    for (const fn of wireListeners) fn(currentState);
    return;
  }
  if (flipTimer !== null) return;
  flipTimer = window.setTimeout(() => {
    flipTimer = null;
    const now = computeState();
    if (now !== currentState) {
      currentState = now;
      for (const fn of wireListeners) fn(currentState);
    }
  }, 800);
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', scheduleWireCheck);
  window.addEventListener('offline', scheduleWireCheck);
  // The dead-wire watchdog. Every four seconds, for every stream that
  // claims to be live, if we haven't seen a byte in 45s, it lied — force
  // a reconnect. This catches the CDN-buffered-connection failure that
  // `es.onerror` never fires on.
  window.setInterval(() => {
    const now = Date.now();
    for (const s of streams) {
      if (s.live && now - s.lastByteAt > SILENT_AFTER_MS) {
        s.reconnect();
      }
    }
    // Also re-evaluated on a timer because reconnecting becomes offline by
    // the passage of time alone, with no event to hang the flip on.
    scheduleWireCheck();
  }, 4000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    // On return-to-foreground, any stream that hasn't received in 25s is
    // suspicious; reconnect it, don't wait for the watchdog's next tick.
    const now = Date.now();
    for (const s of streams) {
      if (now - s.lastByteAt > 25_000) s.reconnect();
    }
    scheduleWireCheck();
  });
}

export function onWireState(fn: WireListener): () => void {
  wireListeners.add(fn);
  fn(currentState);
  return () => wireListeners.delete(fn);
}

export function openStream(
  url: string,
  handlers: Record<string, (data: unknown, id?: string) => void>,
  opts: { lastEventId?: number; onOpen?: () => void } = {},
): StreamHandle {
  let es: EventSource | null = null;
  let closed = false;
  let lastId = opts.lastEventId ?? 0;
  let retryMs = 1000;
  const tracked: TrackedStream = {
    live: false,
    lastByteAt: Date.now(),
    downSince: Date.now(),
    reconnect: () => reconnect(),
  };
  streams.add(tracked);

  const setLive = (v: boolean) => {
    if (tracked.live === v) return;
    tracked.live = v;
    if (v) {
      tracked.lastByteAt = Date.now();
      tracked.downSince = 0;
    } else {
      tracked.downSince = Date.now();
    }
    scheduleWireCheck();
  };

  // Anything the server sends advances the clock: `onopen`, generic
  // messages, every named handler the caller registered, and the daemon's
  // own `hb` event, which is registered below whether the caller asked for
  // it or not. Before V6 the heartbeat was a comment, which EventSource
  // never surfaces, so a quiet stream looked silent and was reconnected on
  // a loop.
  const touch = () => {
    tracked.lastByteAt = Date.now();
    if (!tracked.live) setLive(true);
  };

  const connect = () => {
    if (closed) return;
    const sep = url.includes('?') ? '&' : '?';
    es = new EventSource(lastId ? `${url}${sep}after=${lastId}` : url);
    // Headers arriving is not a heartbeat: a proxy can hold a connection
    // open while dropping every frame. Only a frame advances the clock.
    es.onopen = () => {
      retryMs = 1000;
      // The caller read its state before subscribing; whatever changed in
      // the gap, and whatever went by while a reconnect was pending, is
      // re-read now. The stored frames replay by id, but a fact announced
      // only by an unstored frame would otherwise wait for the next poll.
      opts.onOpen?.();
    };
    es.onmessage = () => touch();
    // The daemon's heartbeat. Never handed to callers — its only job is to
    // prove the wire is moving.
    es.addEventListener('hb', () => touch());
    for (const [event, fn] of Object.entries(handlers)) {
      es.addEventListener(event, (e) => {
        const me = e as MessageEvent;
        touch();
        if (me.lastEventId) lastId = Number(me.lastEventId) || lastId;
        try {
          fn(JSON.parse(me.data), me.lastEventId);
        } catch {
          // an unparseable frame is dropped, not fatal
        }
      });
    }
    es.onerror = () => {
      es?.close();
      es = null;
      setLive(false);
      if (!closed) {
        window.setTimeout(connect, retryMs);
        retryMs = Math.min(retryMs * 2, 15_000);
      }
    };
  };

  const reconnect = () => {
    if (closed) return;
    setLive(false);
    try {
      es?.close();
    } catch {
      // fine
    }
    es = null;
    connect();
  };

  connect();

  return {
    close() {
      closed = true;
      streams.delete(tracked);
      try {
        es?.close();
      } catch {
        // fine
      }
      es = null;
      scheduleWireCheck();
    },
  };
}
